/**
 * Iterate over an Array
 *
 * @param prefix - Prefix of the Unique ID Request
 *
 * @example
 * ```typescript
 * // To get a Unique ID with the prefix of screen
 * getID("screen");
 *
 * ```
 */
import DIContainer from '@components/DependencyInjectionContainer.js';
import { catchError, map } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { htmlToPlaintext } from "@components/Utility/StringUtil.js";
import { replaceTokens } from '@components/Utility/Template.js';
class UniqueIdGenerator {
    static generate(prefix = '') {
        UniqueIdGenerator.counter++;
        return `${prefix}${UniqueIdGenerator.counter}`;
    }
}
UniqueIdGenerator.counter = 0;
export function getID(prefix) {
    return UniqueIdGenerator.generate(prefix);
}
export function copyToClipboard(text, html) {
    if (html && window.ClipboardItem) {
        // Build clipboard payload with HTML + plain text fallback
        const item = new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" })
        });
        navigator.clipboard.write([item])
            .catch(() => navigator.clipboard.writeText(text)); // fallback
    }
    else {
        // Plain text only
        navigator.clipboard.writeText(text);
    }
}
export function copyToClipboardRich(value) {
    const blobHtml = new Blob([value], { type: 'text/html' });
    const blobPlain = new Blob([htmlToPlaintext(value)], { type: 'text/plain' });
    // build the ClipboardItem
    const item = new ClipboardItem({
        'text/html': blobHtml,
        'text/plain': blobPlain
    });
    // write it
    navigator.clipboard.write([item]);
}
export function isTestIM() {
    return typeof window.getTestimStatus !== 'undefined';
}
export function containerAutoResize(container, scope = '.autoResize', maxHeight) {
    let resizableComponents = Array.from(container.querySelectorAll(scope)).filter((element) => {
        let currentElement = element;
        while (currentElement && currentElement !== container) {
            if (window.getComputedStyle(currentElement).display === 'none') {
                return false;
            }
            currentElement = currentElement.parentElement;
        }
        return true;
    }).map((element) => {
        return DIContainer.getInstance('ComponentRegistry').getComponentByElement(element, false);
    });
    const topLevel = new Set(resizableComponents);
    const hasChildren = new Set();
    resizableComponents.forEach((comp) => {
        topLevel.forEach((maybeChild) => {
            if (comp !== maybeChild && comp.rootElement.contains(maybeChild.rootElement)) {
                topLevel.delete(maybeChild);
                hasChildren.add(comp);
            }
        });
    });
    resizableComponents = Array.from(topLevel);
    maxHeight = maxHeight || container.clientHeight;
    const contentHeight = container.scrollHeight;
    const staticHeight = contentHeight - resizableComponents.reduce((acc, component) => acc + component.rootElement.offsetHeight, 0);
    let totalResizableHeight = maxHeight - staticHeight;
    let remaining = Array.from(resizableComponents);
    let found = true;
    const map = {};
    while (found) {
        let crsFound = false;
        let remainingCount = remaining.length;
        remaining.forEach((component) => {
            if (component.minAutoResize > totalResizableHeight / remainingCount) {
                crsFound = true;
                component.height = component.minAutoResize.toString() + 'px';
                totalResizableHeight -= component.minAutoResize;
                remainingCount--;
                map[component.id] = true;
                // remaining = remaining.filter(el => el !== component);
            }
        });
        remaining = remaining.filter(component => !map[component.id]);
        found = crsFound;
    }
    if (remaining.length > 0) {
        const resizableHeightPerComponent = totalResizableHeight / remaining.length;
        remaining.forEach((component) => {
            component.height = resizableHeightPerComponent.toString() + 'px';
            if (hasChildren.has(component)) {
                containerAutoResize(component.rootElement, '.autoResize', resizableHeightPerComponent);
            }
        });
    }
}
export function mapCancelToFalse(source$) {
    return source$.pipe(catchError((err) => {
        if (err && err.isCancel) {
            return of(false);
        }
        return throwError(() => new Error(err.message));
    }));
}
/*
// Example
const aggs = { profit: 500, extendedPrice: 2000 };
console.log( calc("{{profit}}/({{extendedPrice}}-100)*2 + (50/5)", aggs) );
*
*/
export function calc(expr, aggs, options) {
    let filled;
    // 1) substitute {{field}}
    if (options) {
        const aggStrings = {};
        Object.keys(aggs).forEach((key) => {
            aggStrings[key] = aggs[key].toString();
        });
        filled = replaceTokens(expr, aggStrings, options);
    }
    else {
        filled = expr.replace(/\{\{([^}]+?)\}\}/g, (_match, field) => {
            if (!(field in aggs)) {
                throw new Error(`Unknown field “{{${field}}}”`);
            }
            return String(aggs[field]);
        });
    }
    // 2) sanitize
    if (!/^[0-9eE+\-*/().\s]+$/.test(filled)) {
        throw new Error(`Invalid chars in "${filled}"`);
    }
    // 3) tokenize: either a decimal number or exactly one operator/paren
    // const tokens = filled.match(/\d+(\.\d+)?|[+\-*/()]/g)!;
    // const tokens = filled.match(/-?\d+(\.\d+)?|[+\-*/()]/g)!; // handles negative numbers
    const tokens = filled.match(/(?:-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)|[+\-*/()]/g);
    // 4) shunting-yard -> RPN
    const prec = { '+': 1, '-': 1, '*': 2, '/': 2 };
    const output = [];
    const ops = [];
    for (const tok of tokens) {
        if (!isNaN(Number(tok))) {
            output.push(tok);
        }
        else if (tok === '+' || tok === '-' || tok === '*' || tok === '/') {
            while (ops.length &&
                ops[ops.length - 1] !== '(' &&
                prec[ops[ops.length - 1]] >= prec[tok]) {
                output.push(ops.pop());
            }
            ops.push(tok);
        }
        else if (tok === '(') {
            ops.push(tok);
        }
        else if (tok === ')') {
            while (ops.length && ops[ops.length - 1] !== '(') {
                output.push(ops.pop());
            }
            ops.pop(); // discard '('
        }
    }
    while (ops.length) {
        output.push(ops.pop());
    }
    // 5) RPN evaluate
    const stack = [];
    for (const t of output) {
        if (!isNaN(Number(t))) {
            stack.push(parseFloat(t));
        }
        else {
            const b = stack.pop(), a = stack.pop();
            switch (t) {
                case '+':
                    stack.push(a + b);
                    break;
                case '-':
                    stack.push(a - b);
                    break;
                case '*':
                    stack.push(a * b);
                    break;
                case '/':
                    // avoid divide-by-zero → yield 0 instead of Infinity
                    stack.push(b === 0 ? 0 : a / b);
                    break;
            }
        }
    }
    return stack[0];
}
export function getKey(keytype = 'UNQ', initiator) {
    return DIContainer.getInstance('Request').GET({
        endpoint: 'Util',
        action: 'generateKey',
        initiator,
        requestData: {
            keytype
        }
    }).pipe(map((response) => response.payload.outvars.id));
}
export function cssVar(name, element) {
    return getComputedStyle(document.body)
        .getPropertyValue(name)
        .trim();
}
export function hexToRgba(hex, alpha) {
    if (hex.indexOf('#') !== 0) {
        return hex;
    }
    let c = hex.replace('#', '');
    if (c.length === 3) {
        // expand short form (#f00 → #ff0000)
        c = c.split('').map(ch => ch + ch).join('');
    }
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
const HEX_SHORT = /^#([0-9a-f]{3})$/i;
const HEX_LONG = /^#([0-9a-f]{6})$/i;
const RGB_REGEX = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
const RGBA_REGEX = /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d*\.?\d+)\s*\)$/i;
export function setAlpha(color, alpha) {
    // 1) HEX short form, e.g. "#f00"
    let m;
    if ((m = color.match(HEX_SHORT))) {
        const [r, g, b] = m[1].split('').map(ch => parseInt(ch + ch, 16));
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // 2) HEX long form, e.g. "#ff0000"
    if ((m = color.match(HEX_LONG))) {
        const hex = m[1];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // 3) rgb(...)
    if ((m = color.match(RGB_REGEX))) {
        const [, r, g, b] = m;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // 4) rgba(...) — drop the old alpha, replace with new one
    if ((m = color.match(RGBA_REGEX))) {
        const [, r, g, b] = m;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    // 5) Unrecognized format — just return original
    return color;
}
/**
 * Shrinks the font-size on `el` until it fits within its parent.
 * @param el       The element whose text we want to fit.
 * @param maxSize  Upper bound for font-size (in px).
 * @param minSize  Lower bound (in px).
 */
export function fitTextToContainer(el, maxSize, minSize = 8) {
    const parent = el.parentElement;
    let fontSize = maxSize;
    el.style.fontSize = `${fontSize}px`;
    // As long as it overflows, step down
    while (fontSize > minSize &&
        (el.scrollWidth > parent.clientWidth || el.scrollHeight > parent.clientHeight)) {
        fontSize -= 1;
        el.style.fontSize = `${fontSize}px`;
    }
}
export function clamp(n, min, max) { return Math.min(max, Math.max(min, n)); }
export function clampInt(v, min, max) {
    const n = Number.parseInt(v, 10);
    return clamp(Number.isNaN(n) ? min : n, min, max);
}
export function normalizeHex(v) {
    let s = v.trim();
    if (!s)
        return '';
    if (s[0] === '#')
        s = s.slice(1);
    if (s.length === 3)
        s = s.split('').map(c => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(s))
        return '';
    return '#' + s.toUpperCase();
}
export function hexToHsv(hex) {
    const n = normalizeHex(hex);
    if (!n)
        return null;
    const r = parseInt(n.slice(1, 3), 16) / 255;
    const g = parseInt(n.slice(3, 5), 16) / 255;
    const b = parseInt(n.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        switch (max) {
            case r:
                h = 60 * (((g - b) / d) % 6);
                break;
            case g:
                h = 60 * ((b - r) / d + 2);
                break;
            case b:
                h = 60 * ((r - g) / d + 4);
                break;
        }
    }
    if (h < 0)
        h += 360;
    const v = max;
    const s = max === 0 ? 0 : d / max;
    return { h: Math.round(h), s: Math.round(s * 100), v: Math.round(v * 100) };
}
export function hsvToHex({ h, s, v }) {
    const S = clamp(s, 0, 100) / 100;
    const V = clamp(v, 0, 100) / 100;
    const C = V * S;
    const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = V - C;
    let r = 0, g = 0, b = 0;
    if (0 <= h && h < 60)
        [r, g, b] = [C, X, 0];
    else if (60 <= h && h < 120)
        [r, g, b] = [X, C, 0];
    else if (120 <= h && h < 180)
        [r, g, b] = [0, C, X];
    else if (180 <= h && h < 240)
        [r, g, b] = [0, X, C];
    else if (240 <= h && h < 300)
        [r, g, b] = [X, 0, C];
    else
        [r, g, b] = [C, 0, X];
    const R = Math.round((r + m) * 255);
    const G = Math.round((g + m) * 255);
    const B = Math.round((b + m) * 255);
    const toHex = (n) => n.toString(16).padStart(2, '0').toUpperCase();
    return `#${toHex(R)}${toHex(G)}${toHex(B)}`;
}
export function pastelizeColor(hex) {
    const hsv = hexToHsv(hex);
    if (!hsv)
        return hex;
    hsv.s = Math.round(hsv.s * 0.4);
    hsv.v = Math.min(100, Math.round(hsv.v + (100 - hsv.v) * 0.5));
    return hsvToHex(hsv) || hex;
}
export function isColorDark(hex) {
    const n = normalizeHex(hex);
    if (!n)
        return false;
    const r = parseInt(n.slice(1, 3), 16) / 255;
    const g = parseInt(n.slice(3, 5), 16) / 255;
    const b = parseInt(n.slice(5, 7), 16) / 255;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 0.4;
}
export function darkenColor(hex, factor) {
    if (!hex || hex.charAt(0) !== '#')
        return hex;
    let c = hex.replace('#', '');
    if (c.length === 3) {
        c = c.split('').map(ch => ch + ch).join('');
    }
    const r = Math.round(parseInt(c.substring(0, 2), 16) * factor);
    const g = Math.round(parseInt(c.substring(2, 4), 16) * factor);
    const b = Math.round(parseInt(c.substring(4, 6), 16) * factor);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
export function randInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}
export function stringifySafe(value) {
    if (typeof value === 'string')
        return value;
    try {
        return JSON.stringify(value, (_k, v) => {
            // Drop huge objects/functions
            if (typeof v === 'function')
                return `[Function ${v.name || 'anonymous'}]`;
            return v;
        });
    }
    catch {
        try {
            return String(value);
        }
        catch {
            return '[Unserializable]';
        }
    }
}
export function stringifySort(value) {
    if (value === null || typeof value !== "object")
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map(stringifySort).join(",")}]`;
    const obj = value;
    return `{${Object.keys(obj).sort().map(k => `${JSON.stringify(k)}:${stringifySort(obj[k])}`).join(",")}}`;
}
export function shortDeterministicId(value, length = 13 // ~67 bits in base36
) {
    const s = stringifySort(value);
    let hash = 0xcbf29ce484222325n; // FNV-1a 64-bit offset
    const prime = 0x100000001b3n;
    for (let i = 0; i < s.length; i++) {
        hash ^= BigInt(s.charCodeAt(i));
        hash = (hash * prime) & 0xffffffffffffffffn;
    }
    // base36 (0-9a-z) for compactness; pad to keep fixed width
    const base36 = hash.toString(36).padStart(13, "0");
    return base36.slice(0, length);
}
export function moneyToCents(v) {
    if (v == null)
        return 0;
    let s = String(v).trim();
    // negatives like "-123.45" or "(123.45)"
    let sign = 1;
    if (/^\(.*\)$/.test(s)) {
        sign = -1;
        s = s.slice(1, -1);
    }
    if (s.startsWith('+'))
        s = s.slice(1);
    if (s.startsWith('-')) {
        sign = -1;
        s = s.slice(1);
    }
    s = s.replace(/,/g, ''); // remove thousands separators
    const [intPart, fracPartRaw = ''] = s.split('.');
    const centsPart = (fracPartRaw + '00').slice(0, 2); // pad/truncate to 2dp
    const cents = (parseInt(intPart || '0', 10) * 100) +
        parseInt(centsPart || '0', 10);
    return sign * (Number.isFinite(cents) ? cents : 0);
}
export function isOverflowingEllipsis(box) {
    // First try: DPR-aware compare to avoid 105==105 rounding issues
    const dpr = window.devicePixelRatio || 1;
    const sw = Math.round(box.scrollWidth * dpr);
    const cw = Math.round(box.clientWidth * dpr);
    if (sw > cw)
        return true;
    // If equal, try a more precise text width measurement as a fallback
    // (handles fractional widths/fonts/zoom weirdness).
    const cs = getComputedStyle(box);
    const borderX = parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
    const innerW = box.getBoundingClientRect().width - borderX;
    const range = document.createRange();
    range.selectNodeContents(box);
    const textW = range.getBoundingClientRect().width;
    range.detach?.();
    // Tiny epsilon
    return textW > innerW;
}
export function isCtrlKey(event) {
    return event.ctrlKey || event.metaKey;
}
export function roundTo(n, decimals) {
    const f = 10 ** decimals;
    return Math.round((n + Number.EPSILON) * f) / f;
}
export function parseVersion(v) {
    const m = (v || '').match(/^(\d+)\.(\d+)\.(\d+)$/);
    if (!m)
        return [0, 0, 0];
    return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
}
export function versionGTE(version, minVersion) {
    const [a1, a2, a3] = parseVersion(version);
    const [b1, b2, b3] = parseVersion(minVersion);
    if (a1 !== b1)
        return a1 > b1;
    if (a2 !== b2)
        return a2 > b2;
    return a3 >= b3;
}
